/**
 * Admin Payments Page - Dashboard with analytics and pending payments verification.
 * Two views: Dashboard (analytics) and Pending (verification queue).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { PageLoader } from "../../components/PageLoader.tsx";
import { Loader } from "../../components/Loader.tsx";
import {
  IconAlertTriangle,
  IconRefresh,
  IconCalendar,
  IconCamera,
  IconBan,
  IconCheck,
  IconClock,
  IconChartBar,
} from "../../components/Icons.tsx";
import {
  getTelegram,
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../lib/telegram.ts";
import { getAuthHeaders, getErrorMessage } from "../../lib/graphql.ts";
import { config } from "../../config.ts";
import { formatDateBangkok } from "../../lib/bangkok-time.ts";
import { getCurrencySymbol } from "../../lib/format.ts";
import { formatCustomerName } from "../../features/admin/shared/types.ts";
import {
  PaymentsDashboard,
  usePendingPayments,
  useApprovePayment,
  useRejectPayment,
} from "../../features/admin/payments/index.ts";

// =============================================================================
// Types
// =============================================================================

type ViewMode = "dashboard" | "pending";

// =============================================================================
// Receipt Viewer (fullscreen with actions)
// =============================================================================

interface ReceiptViewerProps {
  orderId: string;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  isProcessing: boolean;
}

function ReceiptViewer({
  orderId,
  onClose,
  onApprove,
  onReject,
  isProcessing,
}: ReceiptViewerProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToolbar, setShowToolbar] = useState(true);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    setError(null);
    setImageUrl(null);

    const url = `${config.apiUrl}/admin/receipt/${orderId}`;
    fetch(url, { headers: getAuthHeaders(), signal: abortController.signal })
      .then((res) => {
        if (!res.ok)
          throw new Error(
            res.status === 401
              ? "Нужна авторизация"
              : "Не удалось загрузить чек",
          );
        return res.blob();
      })
      .then((blob) => {
        if (abortController.signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setImageUrl(objectUrl);
      })
      .catch((err) => {
        if (abortController.signal.aborted) return;
        setError(getErrorMessage(err, "Ошибка загрузки"));
      })
      .finally(() => {
        if (abortController.signal.aborted) return;
        setLoading(false);
      });

    return () => {
      abortController.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [orderId]);

  const handleImageTap = () => {
    if (!loading && !error) {
      setShowToolbar((prev) => !prev);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* Top header bar */}
      <div
        className={`absolute top-0 left-0 right-0 z-20 transition-all duration-200 ${
          showToolbar && !isProcessing
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-full pointer-events-none"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Back button */}
            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white active:bg-white/20"
            >
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M19 12H5M12 19L5 12L12 5" />
              </svg>
            </button>

            {/* Spacer for symmetry */}
            <div className="w-10" />
          </div>
        </div>
      </div>

      {/* Main image area */}
      <div className="flex-1 relative overflow-hidden" onClick={handleImageTap}>
        {/* Image content */}
        {imageUrl && !loading && !error && (
          <div className="absolute inset-0 overflow-y-auto overscroll-contain bg-slate-100">
            <img
              src={imageUrl}
              alt="Чек"
              className="w-full h-auto select-none"
              draggable={false}
            />
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
            <div className="text-center">
              <Loader className="mx-auto mb-4" />
              <p className="text-white text-[16px] font-medium">Обработка...</p>
              <p className="text-white/60 text-[13px] mt-1">Проверка платежа</p>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && !isProcessing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center">
              <Loader className="mx-auto mb-3" />
              <p className="text-white/60 text-[14px]">Загрузка...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900 px-8">
            <div className="text-center">
              <IconAlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <p className="text-white text-[16px] font-medium mb-2">{error}</p>
              <p className="text-white/60 text-[14px] mb-6">
                Не удалось загрузить изображение чека
              </p>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-white/10 text-white rounded-xl font-medium text-[14px] active:bg-white/20"
              >
                Назад
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom toolbar */}
      <div
        className={`relative z-20 transition-all duration-200 ${
          showToolbar && !isProcessing
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-full pointer-events-none"
        }`}
      >
        <div className="bg-gradient-to-t from-black/80 to-black/60 backdrop-blur-sm">
          <div
            className="flex items-center justify-around px-4 py-3"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            }}
          >
            {/* Reject button */}
            <button
              type="button"
              onClick={onReject}
              disabled={isProcessing || loading}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
            >
              <IconBan className="w-6 h-6 text-red-400" />
              <span className="text-[11px] text-white/80 font-medium">
                Отклонить
              </span>
            </button>

            {/* Approve button */}
            <button
              type="button"
              onClick={onApprove}
              disabled={isProcessing || loading}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
            >
              <IconCheck className="w-6 h-6 text-green-400" />
              <span className="text-[11px] text-white/80 font-medium">
                Подтвердить
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Reject Modal
// =============================================================================

function RejectModal({
  onConfirm,
  onCancel,
  isLoading,
}: {
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [reason, setReason] = useState("");

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 xs:p-4"
      onClick={onCancel}
    >
      <div
        className="card p-3 xs:p-4 sm:p-5 max-w-[calc(100%-0.5rem)] xs:max-w-sm sm:max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm xs:text-base sm:text-lg font-semibold text-slate-900 mb-2 xs:mb-3 sm:mb-4">
          Причина отклонения
        </h3>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Укажите причину отклонения..."
          className="w-full p-2.5 xs:p-3 border border-slate-200 rounded-lg xs:rounded-xl resize-none h-20 xs:h-24 mb-2 xs:mb-3 sm:mb-4 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] text-sm xs:text-base text-slate-900 placeholder:text-slate-400"
          autoFocus
        />
        <div className="flex gap-2 xs:gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-2.5 xs:py-3 px-2 xs:px-3 sm:px-4 bg-slate-100 text-slate-700 rounded-lg xs:rounded-xl font-medium min-h-[40px] xs:min-h-[44px] text-sm"
          >
            Отмена
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || isLoading}
            className="flex-1 py-2.5 xs:py-3 px-2 xs:px-3 sm:px-4 bg-red-500 text-white rounded-lg xs:rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-1.5 xs:gap-2 min-h-[40px] xs:min-h-[44px] text-sm"
          >
            {isLoading ? (
              <>
                <div className="w-3.5 h-3.5 xs:w-4 xs:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="hidden xs:inline">Отклонение...</span>
                <span className="xs:hidden">...</span>
              </>
            ) : (
              "Отклонить"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Pending Payments View
// =============================================================================

interface PendingPaymentsViewProps {
  onCountChange?: (count: number) => void;
}

function PendingPaymentsView({ onCountChange }: PendingPaymentsViewProps) {
  // URQL hooks
  const {
    payments,
    isLoading,
    error: paymentsError,
    refetch: refetchPayments,
  } = usePendingPayments();

  const { approvePayment } = useApprovePayment();
  const { rejectPayment } = useRejectPayment();

  const [processingId, setProcessingId] = useState<string | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);
  const [rejectingPayment, setRejectingPayment] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get("orderId");
  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const error = paymentsError?.message ?? null;

  // Update count when payments change
  useEffect(() => {
    onCountChange?.(payments.length);
  }, [payments.length, onCountChange]);

  // Scroll to order when opened via URL
  useEffect(() => {
    if (!orderIdFromUrl || payments.length === 0) return;
    const found = payments.some((p) => p.id === orderIdFromUrl);
    if (!found) return;
    const el = scrollToRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("orderId");
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [orderIdFromUrl, payments, setSearchParams]);

  // Approve payment
  const handleApprove = useCallback(
    async (orderId: string) => {
      const confirmMessage =
        "Подтвердить оплату? Билеты будут выданы автоматически.";

      const performApprove = async () => {
        setProcessingId(orderId);
        haptic("light");

        try {
          const result = await approvePayment(orderId);

          if (result) {
            haptic("success");
            const message = result.message ?? "Оплата подтверждена";
            safeShowAlert(message);
            refetchPayments();
          } else {
            haptic("error");
          }
        } catch (err) {
          haptic("error");
          safeShowAlert(getErrorMessage(err, "Ошибка подтверждения"));
        } finally {
          setProcessingId(null);
        }
      };

      safeShowConfirm(confirmMessage, (confirmed: boolean) => {
        if (confirmed) performApprove();
      });
    },
    [approvePayment, refetchPayments],
  );

  // Reject payment
  const handleReject = useCallback(
    async (orderId: string, reason: string) => {
      setProcessingId(orderId);
      haptic("light");

      try {
        const result = await rejectPayment(orderId, reason);

        if (result) {
          haptic("success");
          const message = result.message ?? "Оплата отклонена";
          safeShowAlert(message);
          refetchPayments();
        } else {
          haptic("error");
        }
        setRejectingPayment(null);
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка отклонения"));
      } finally {
        setProcessingId(null);
      }
    },
    [rejectPayment, refetchPayments],
  );

  if (isLoading) {
    return <PageLoader message="Загрузка..." />;
  }

  if (error) {
    return (
      <div className="card p-4 xs:p-5 sm:p-6 text-center">
        <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 xs:mb-4 text-amber-500" />
        <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
          Ошибка загрузки
        </h2>
        <p className="text-slate-600 mb-4 xs:mb-6 text-sm">{error}</p>
        <button onClick={refetchPayments} className="btn-primary w-full">
          Попробовать снова
        </button>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="card p-4 xs:p-5 sm:p-6 text-center">
        <div className="w-12 h-12 xs:w-14 xs:h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 xs:mb-4">
          <IconCheck className="w-6 h-6 xs:w-7 xs:h-7 text-green-600" />
        </div>
        <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
          Всё проверено
        </h2>
        <p className="text-xs xs:text-sm text-slate-500">
          Нет заявок, ожидающих проверки
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="card p-3 xs:p-4 mb-4 xs:mb-6 flex items-center justify-between bg-amber-50 border-amber-100">
        <span className="inline-flex items-center gap-1.5 xs:gap-2 text-amber-800 font-medium text-xs xs:text-sm">
          <IconClock className="w-4 h-4 xs:w-5 xs:h-5" />
          {payments.length}{" "}
          {payments.length === 1
            ? "заявка"
            : payments.length < 5
              ? "заявки"
              : "заявок"}{" "}
          на проверке
        </span>
        <button
          onClick={refetchPayments}
          className="flex items-center gap-1.5 xs:gap-2 text-xs xs:text-sm text-amber-700 hover:text-amber-900 transition-colors"
        >
          <IconRefresh className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
          Обновить
        </button>
      </div>

      {/* Payments list */}
      <div className="space-y-3 xs:space-y-4">
        {payments.map((payment) => {
          const totalQuantity = payment.items.reduce(
            (sum, item) => sum + item.quantity,
            0,
          );
          const isProcessing = processingId === payment.id;

          const isHighlighted = orderIdFromUrl === payment.id;
          return (
            <div
              key={payment.id}
              ref={isHighlighted ? scrollToRef : undefined}
              className={`card p-3 xs:p-4 sm:p-5 transition-all ${
                isHighlighted ? "ring-2 ring-amber-400 ring-offset-2" : ""
              }`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3 xs:mb-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-slate-900 text-sm xs:text-base truncate">
                      {formatCustomerName(payment.customer)}
                    </span>
                    <span className="text-[10px] xs:text-xs text-slate-400 font-mono">
                      #{payment.id.slice(-6).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[10px] xs:text-xs text-slate-400">
                    {formatDateBangkok(payment.createdAt)}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <div className="font-bold text-slate-900 text-base xs:text-lg sm:text-xl">
                    {payment.totalAmount} {getCurrencySymbol(payment.currency)}
                  </div>
                  <div className="text-[10px] xs:text-xs text-slate-500 mt-0.5">
                    {totalQuantity}{" "}
                    {totalQuantity === 1
                      ? "билет"
                      : totalQuantity < 5
                        ? "билета"
                        : "билетов"}
                  </div>
                </div>
              </div>

              {/* Event */}
              <div className="mb-3 xs:mb-4 p-2.5 xs:p-3 bg-slate-50 rounded-lg xs:rounded-xl">
                <div className="text-xs xs:text-sm font-medium text-slate-900 mb-0.5 xs:mb-1 truncate">
                  {payment.event.title}
                </div>
                <div className="flex items-center gap-1 xs:gap-1.5 text-[10px] xs:text-xs text-slate-500">
                  <IconCalendar className="w-3 h-3 xs:w-3.5 xs:h-3.5" />
                  {formatDateBangkok(payment.event.startsAt)}
                </div>
                <div className="text-[10px] xs:text-xs text-slate-500 mt-0.5 xs:mt-1 truncate">
                  {payment.items.map((item, idx) => (
                    <span key={idx}>
                      {item.ticketType.name} × {item.quantity}
                      {idx < payment.items.length - 1 && ", "}
                    </span>
                  ))}
                </div>
              </div>

              {/* Receipt */}
              {payment.receiptUrl && (
                <button
                  onClick={() => setViewingReceipt(payment.id)}
                  className="w-full mb-3 xs:mb-4 py-2.5 xs:py-3 px-3 xs:px-4 bg-blue-50 text-blue-700 rounded-lg xs:rounded-xl text-xs xs:text-sm font-medium flex items-center justify-center gap-1.5 xs:gap-2 hover:bg-blue-100 transition-colors"
                >
                  <IconCamera className="w-4 h-4 xs:w-5 xs:h-5" />
                  Посмотреть чек
                  {payment.receiptUploadedAt && (
                    <span className="text-blue-500 text-[10px] xs:text-xs hidden xs:inline">
                      ({formatDateBangkok(payment.receiptUploadedAt)})
                    </span>
                  )}
                </button>
              )}

              {/* Actions */}
              <div className="flex gap-2 xs:gap-3">
                <button
                  onClick={() => setRejectingPayment(payment.id)}
                  disabled={isProcessing}
                  className="flex-1 py-2.5 xs:py-3 px-2 xs:px-4 bg-red-50 text-red-600 rounded-lg xs:rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-1 xs:gap-2 hover:bg-red-100 transition-colors text-xs xs:text-sm"
                >
                  <IconBan className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
                  <span className="hidden xs:inline">Отклонить</span>
                  <span className="xs:hidden">Откл.</span>
                </button>
                <button
                  onClick={() => handleApprove(payment.id)}
                  disabled={isProcessing}
                  className="flex-1 py-2.5 xs:py-3 px-2 xs:px-4 bg-green-500 text-white rounded-lg xs:rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-1 xs:gap-2 hover:bg-green-600 transition-colors text-xs xs:text-sm"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-3.5 h-3.5 xs:w-4 xs:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ...
                    </>
                  ) : (
                    <>
                      <IconCheck className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
                      <span className="hidden xs:inline">Подтвердить</span>
                      <span className="xs:hidden">OK</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Receipt viewer (fullscreen with actions) - rendered via Portal to escape Layout */}
      {viewingReceipt &&
        createPortal(
          <ReceiptViewer
            orderId={viewingReceipt}
            onClose={() => setViewingReceipt(null)}
            onApprove={() => {
              const orderId = viewingReceipt;
              setViewingReceipt(null);
              handleApprove(orderId);
            }}
            onReject={() => {
              const orderId = viewingReceipt;
              setViewingReceipt(null);
              setRejectingPayment(orderId);
            }}
            isProcessing={processingId === viewingReceipt}
          />,
          document.body,
        )}

      {rejectingPayment && (
        <RejectModal
          isLoading={processingId === rejectingPayment}
          onConfirm={(reason) => handleReject(rejectingPayment, reason)}
          onCancel={() => setRejectingPayment(null)}
        />
      )}
    </>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function AdminPaymentsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [searchParams] = useSearchParams();

  // Determine initial view from URL
  const viewFromUrl = searchParams.get("view");
  const [viewMode, setViewMode] = useState<ViewMode>(
    viewFromUrl === "pending" ? "pending" : "dashboard",
  );

  // Pending count for tab badge (fetched via hook)
  const { payments: pendingPaymentsForCount } = usePendingPayments();
  const [pendingCount, setPendingCount] = useState<number>(0);

  // Update pending count when payments change
  useEffect(() => {
    setPendingCount(pendingPaymentsForCount.length);
  }, [pendingPaymentsForCount.length]);

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const handleViewChange = (mode: ViewMode) => {
    haptic("light");
    setViewMode(mode);
  };

  const handleNavigateToPending = () => {
    handleViewChange("pending");
  };

  return (
    <Layout>
      {/* Header */}
      <h1 className="page-title">Платежи</h1>

      {/* View mode tabs */}
      <div className="flex p-1 bg-slate-100 rounded-xl mb-4">
        <button
          type="button"
          onClick={() => handleViewChange("dashboard")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-medium transition-all ${
            viewMode === "dashboard"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <IconChartBar className="w-4 h-4" />
          Аналитика
        </button>
        <button
          type="button"
          onClick={() => handleViewChange("pending")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[13px] font-medium transition-all ${
            viewMode === "pending"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <IconClock className="w-4 h-4" />
          На проверке
          {pendingCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[11px] font-semibold bg-amber-500 text-white rounded-full min-w-[20px] text-center">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {viewMode === "dashboard" ? (
        <PaymentsDashboard onNavigateToPending={handleNavigateToPending} />
      ) : (
        <PendingPaymentsView onCountChange={setPendingCount} />
      )}
    </Layout>
  );
}
